"use client";
import { CSSProperties, JSX, RefObject, useEffect, useRef, useState } from "react";
import {exo2Bold, exo2Medium, exo2SemiBold, montserratRegular} from "@@/utils/fonts";
import useAppearOnScroll from "@@/hooks/useAppearsOnScroll";
import ProgressiveImage from "@@/utils/ProgressiveImage";
import { StaticImageData } from "next/image";
import styles from "@@/styles/components/shared/StepperHorizontal.module.scss";
import Button from "@@/components/ui/Button";

interface Props {
    title: string;
    subtitle?: string;
    text?: string;
    btn?: {
        text: string;
        url: string;
    }
    items: {
        title: string;
        text: string;
        img: string;
        animation?: {
            ref: RefObject<HTMLLIElement | null>;
            className: string;
            style: CSSProperties;
        }[];
    }[];
    images?: (StaticImageData | undefined)[];
    images4k?: (StaticImageData | undefined)[];
    sectionId?: string;
    stepperMiddle?: boolean;
    className?: string;
    stepperClassName?: string;
    imgClassName?: string;
    cardClassName?: string;
    btnColor?: string;
    btnColorReverse?: string;
    cardItemClassName?: string;
}

/** Ajout : type pour nos variables CSS custom */
type StepperCSSVars = {
    ["--btn-color"]?: string;
    ["--btn-color-reverse"]?: string;
};

export default function StepperHorizontal({
                                              title,
                                              subtitle,
                                              text,
                                              btn,
                                              items,
                                              sectionId,
                                              images,
                                              images4k,
                                              stepperMiddle,
                                              className,
                                              stepperClassName,
                                              imgClassName,
                                              cardClassName,
                                              btnColor,
                                              btnColorReverse,
                                              cardItemClassName
                                          }: Props): JSX.Element {
    const [activeIndex, setActiveIndex] = useState(0);

    const titleAnim = useAppearOnScroll<HTMLDivElement>({ from: "fromBottom", duration: 800, delay: 0 });

    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const sectionRef = useRef<HTMLElement | null>(null);

    const [isVisible, setIsVisible] = useState(true);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [maxContentHeight, setMaxContentHeight] = useState<number>(0);

    const animatedItems = Array.isArray(items)
        ? items.map((item, index) => {
            const animation = useAppearOnScroll<HTMLLIElement>({
                from: "fromBottom",
                duration: 500,
                delay: index * 100,
                offset: index * 100,
            });
            return { ...item, animation };
        })
        : [];

    const animatedContent = useAppearOnScroll<HTMLElement>({
        from: "fromRight",
        duration: 800,
        delay: 0,
    });

    useEffect(() => {
        const nodes = contentRefs.current.filter(Boolean) as HTMLDivElement[];
        if (!nodes.length) return;

        let raf = 0;
        const heights = new Map<Element, number>();

        const scheduleUpdate = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                let max = 0;
                heights.forEach(h => { if (h > max) max = h; });
                setMaxContentHeight(max);
            });
        };

        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const h =
                    (Array.isArray(entry.contentBoxSize)
                        ? entry.contentBoxSize[0]?.blockSize
                        : (entry as any).contentBoxSize?.blockSize) ??
                    (entry.target as HTMLElement).clientHeight;

                heights.set(entry.target, h || 0);
            }
            scheduleUpdate();
        });

        nodes.forEach(n => ro.observe(n));
        scheduleUpdate();

        return () => {
            ro.disconnect();
            cancelAnimationFrame(raf);
        };
    }, [items.length]);

    useEffect(() => {
        const sectionEl = sectionRef.current;
        if (!sectionEl) return;

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.intersectionRatio >= 0.6);
            },
            { threshold: [0, 0.6, 1] }
        );

        observerRef.current.observe(sectionEl);

        return () => {
            observerRef.current?.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!isVisible) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        intervalRef.current = setInterval(() => {
            setActiveIndex((prevIndex) => (prevIndex + 1) % animatedItems.length);
        }, 5000);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [animatedItems.length, isVisible]);

    const handleClick = (index: number) => {
        setActiveIndex(index);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
                setActiveIndex((prevIndex) => (prevIndex + 1) % animatedItems.length);
            }, 5000);
        }
    };

    return (
        <section id={sectionId} ref={sectionRef} className={`px-11 w-100 ${className}`}>
            <div
                className={`bg-blue-light p-5 w-100 p-2-mobile d-flex ${
                    stepperMiddle ? styles.stepperMiddle : styles.stepperDefault
                } gap-5 rounded-1 overflow-hidden position-relative ${cardClassName}`}
            >
                <div
                    ref={titleAnim.ref}
                    className={`${styles.stepperTitle} d-flex flex-column gap-2 ${titleAnim.className}`}
                    style={titleAnim.style}
                >
                    <h2 className={`${exo2Bold.className}`} dangerouslySetInnerHTML={{ __html: title }} />
                    {subtitle && <div className={`${exo2Medium.className}`} dangerouslySetInnerHTML={{__html: subtitle}}></div>}
                    {text && <p dangerouslySetInnerHTML={{__html: text}}></p>}
                    {btn && <Button href={btn.url} title={btn.text} className={`btn`} bgColor={`var(--blue-600)`} textColor={'var(--grey-000)'}></Button>}
                </div>

                <div className={`d-flex gap-5 ${styles.stepper} ${stepperClassName} overflow-hidden`} style={{ ...animatedContent.style, minHeight:`${maxContentHeight || 1}px` }}>
                    {/* Liste des titres */}
                    <ul className={`list-none d-flex flex-column align-stretch gap-1 width-fit-content ${styles.stepperBtnList} ${stepperClassName}`}>
                        {animatedItems.map((item, index) => {
                            const liStyle: CSSProperties & StepperCSSVars = {
                                ...(item.animation.style as CSSProperties),
                                ["--btn-color"]: btnColor,
                                ["--btn-color-reverse"]: btnColorReverse,
                            };

                            return (
                                <li
                                    key={index}
                                    ref={item.animation.ref}
                                    className={`d-flex flex-column w-100 gap-1 ${styles.stepperBtn} ${item.animation.className} ${
                                        index === activeIndex ? styles.active : ""
                                    }`}
                                    style={liStyle}
                                    onClick={() => handleClick(index)}
                                >
                                    <div className={`button secondary d-flex gap-1 rounded-5 align-items-center ${styles.Button}`}>
                                        <span
                                            className={`d-flex justify-center align-items-center ${styles.Number} ${exo2SemiBold.className}`}
                                            dangerouslySetInnerHTML={{
                                                __html: `${
                                                    !stepperMiddle
                                                        ? `${index + 1}`
                                                        : `<svg xmlns="http://www.w3.org/2000/svg" class="${styles.vIcon}" width="18" height="12" viewBox="0 0 18 12" fill="none">
                                                    <path d="M1 4.5L7.5 11L17.5 1" stroke="white" stroke-linecap="round"/>
                                                  </svg>`
                                                }`,
                                            }}
                                        />
                                        <p className={styles.btnText}>{item.title}</p>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Bloc de contenu actif */}
                    <div
                        ref={animatedContent.ref as RefObject<HTMLDivElement>}
                        className={`${styles.stepperContainer} ${animatedContent.className} ${cardItemClassName}`}
                        style={animatedContent.style}
                    >
                        {animatedItems.map((item, index) => (
                            <div
                                key={index}
                                ref={(el) => {
                                    contentRefs.current[index] = el;
                                }}
                                className={`duration-500 ease-in-out mx-height-100 ${styles.stepperContent} ${
                                    index === activeIndex ? styles.active : index === activeIndex - 1 ? styles.previous : ""
                                } `}
                            >
                                <div className={`d-flex flex-column gap-2 justify-center min-h-fit ${!stepperMiddle ? 'flex-1' : ''}`}>
                                    <h3 className={`${exo2SemiBold.className} ${styles.contentTitle}`}>
                                        {stepperMiddle ? `${index + 1}. ${item.title}` : `${item.title}`}
                                    </h3>
                                    {item.text && <p className={montserratRegular.className} dangerouslySetInnerHTML={{ __html: item.text }} />}
                                </div>

                                {images?.[index] && images4k?.[index] && (
                                    <ProgressiveImage
                                        alt={item.img}
                                        width={images4k[index]!.width}
                                        height={images4k[index]!.height}
                                        quality={60}
                                        lowSrc={images[index]!}
                                        highSrc={images4k[index]!}
                                        classLowRes={`df`}
                                        className={`position-relative mx-height-100 ${
                                            stepperMiddle ? `w-100-with-img` : `h-100-with-img flex-1`
                                        } overflow-hidden visibile-mobile rounded-1 min-h-10 mx-w-100-mobile ${imgClassName}`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
